import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { adminService } from '../../../services/adminService';
import { extractList, extractMeta } from '../../../services/api';
import { Loading, EmptyState } from '../../../shared/components';
import { ROLES } from '../../../constants';
import './AdminCommonStyles.scss';

const GenderBadge = ({ value }) => {
  const map = {
    female: { label: 'Perempuan', cls: 'gender-badge gender-badge--female' },
    male:   { label: 'Laki-laki', cls: 'gender-badge gender-badge--male' },
    all:    { label: 'Semua',     cls: 'gender-badge gender-badge--all' },
  };
  const { label, cls } = map[value] || { label: value, cls: 'gender-badge gender-badge--all' };
  return <span className={cls}>{label}</span>;
};

const StatusBadge = ({ value }) => {
  const active = value === 'active';
  return (
    <span className={`status-badge status-badge--${active ? 'active' : 'inactive'}`}>
      {active ? 'Aktif' : 'Nonaktif'}
    </span>
  );
};

const UniformsPage = () => {
  const { user } = useAuth();
  const isSuperadmin = user?.role === ROLES.SUPERADMIN;
  const [uniforms, setUniforms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({});
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formCategoryList, setFormCategoryList] = useState([]);
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };
  const [formData, setFormData] = useState({
    uniform_category_id: '',
    code: '',
    name: '',
    size: '',
    color: '',
    gender_target: 'all',
    description: '',
    status: 'active',
    company_id: '',
  });

  useEffect(() => {
    fetchUniforms();
    fetchCategories();
    if (isSuperadmin) {
      adminService.getCompanies({ limit: 100 })
        .then((res) => setCompanies(extractList(res.data)))
        .catch(() => showToast('Gagal memuat daftar perusahaan'));
    }
  }, [page, search]);

  const fetchUniforms = async () => {
    setLoading(true);
    try {
      const res = await adminService.getUniforms({ page, limit: 10, search });
      setUniforms(extractList(res.data));
      setPageInfo(extractMeta(res.data));
    } catch (err) {
      showToast(err?.message || 'Gagal memuat data seragam');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await adminService.getUniformCategories({ limit: 100 });
      setCategories(extractList(res.data));
    } catch (err) {
      showToast(err?.message || 'Gagal memuat kategori seragam');
    }
  };

  // Fetch categories filtered by a specific company_id (for the add form)
  const fetchFormCategories = async (companyId) => {
    if (!companyId) { setFormCategoryList([]); return; }
    try {
      const res = await adminService.getUniformCategories({ limit: 100, company_id: companyId });
      setFormCategoryList(extractList(res.data));
    } catch (err) {
      setFormCategoryList([]);
      showToast(err?.message || 'Gagal memuat kategori untuk perusahaan ini');
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    const defaultCompanyId = isSuperadmin ? '' : (user?.company_id || '');
    setFormData({
      uniform_category_id: '', code: '', name: '', size: '', color: '',
      gender_target: 'all', description: '', status: 'active',
      company_id: defaultCompanyId,
    });
    setFormCategoryList([]);
    if (!isSuperadmin && defaultCompanyId) fetchFormCategories(defaultCompanyId);
    setShowForm(true);
  };

  const handleEdit = async (id) => {
    const res = await adminService.getUniformById(id);
    if (res.status) {
      setFormData(res.data);
      setEditingId(id);
      setShowForm(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code || !formData.name || !formData.uniform_category_id) {
      showToast('Kode, nama, dan kategori harus diisi');
      return;
    }
    if (isSuperadmin && !editingId && !formData.company_id) {
      showToast('Pilih perusahaan terlebih dahulu');
      return;
    }
    try {
      const payload = { ...formData };
      // API expects integers — HTML select values are always strings
      if (payload.uniform_category_id) payload.uniform_category_id = Number(payload.uniform_category_id);
      if (payload.company_id) payload.company_id = Number(payload.company_id);
      if (!isSuperadmin) delete payload.company_id;
      const res = editingId
        ? await adminService.updateUniform(editingId, payload)
        : await adminService.createUniform(payload);
      if (res.status) {
        fetchUniforms();
        setShowForm(false);
      } else {
        showToast(res.message || 'Gagal menyimpan seragam');
      }
    } catch (err) {
      showToast('Gagal menyimpan seragam');
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Hapus seragam "${name}"? Tindakan ini tidak bisa dibatalkan.`)) {
      try {
        const res = await adminService.deleteUniform(id);
        if (res.status) fetchUniforms();
        else showToast(res.message || 'Gagal menghapus seragam');
      } catch (err) {
        showToast(err?.message || 'Gagal menghapus seragam');
      }
    }
  };

  // Client-side filter (on top of search)
  const filtered = uniforms.filter((u) => {
    if (filterCategory && String(u.uniform_category_id) !== String(filterCategory)) return false;
    if (filterGender && u.gender_target !== filterGender) return false;
    if (filterStatus && u.status !== filterStatus) return false;
    return true;
  });

  if (loading) return <Loading />;

  return (
    <div className="page-container page-container--full">
      {toast && <div className="toast">{toast}</div>}
      <div className="page-header">
        <h1>Daftar Seragam</h1>
        <p>Kelola semua seragam yang tersedia</p>
      </div>

      {/* Controls: search + filters + add button */}
      <div className="page-controls" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
        <div className="search-icon-wrap">
          <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Cari kode, nama, perusahaan..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="filter-select"
        >
          <option value="">Semua Kategori</option>
          {categories.map((c) => <option key={c.uniform_category_id} value={c.uniform_category_id}>{c.name}</option>)}
        </select>

        <select
          value={filterGender}
          onChange={(e) => setFilterGender(e.target.value)}
          className="filter-select"
        >
          <option value="">Semua Gender</option>
          <option value="male">Laki-laki</option>
          <option value="female">Perempuan</option>
          <option value="all">Semua</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="">Semua Status</option>
          <option value="active">Aktif</option>
          <option value="inactive">Nonaktif</option>
        </select>

        <button className="btn-primary" onClick={handleAdd} style={{ boxShadow: '0 4px 12px rgba(18,183,173,0.3)', whiteSpace: 'nowrap' }}>
          + Tambah Seragam
        </button>
      </div>

      {filtered.length === 0 ? (
        <EmptyState message="Tidak ada seragam" />
      ) : (
        <div className="table-card">
          <table className="data-table uniforms-table">
            <thead>
              <tr>
                <th>Seragam</th>
                {isSuperadmin && <th>Perusahaan</th>}
                <th>Kategori</th>
                <th>Target</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.uniform_id}>
                  <td>
                    <div className="uniform-name-cell">
                      <span className="uniform-code">{u.code}</span>
                      <span className="uniform-name">{u.name}</span>
                      {(u.size || u.color) && (
                        <span className="uniform-sub">
                          {[u.size && `Ukuran ${u.size}`, u.color].filter(Boolean).join(' \u00B7 ')}
                        </span>
                      )}
                    </div>
                  </td>
                  {isSuperadmin && (
                    <td className="text-muted">{u.company?.name || '\u2014'}</td>
                  )}
                  <td className="text-muted">
                    {categories.find((c) => c.uniform_category_id === u.uniform_category_id)?.name || '\u2014'}
                  </td>
                  <td><GenderBadge value={u.gender_target} /></td>
                  <td><StatusBadge value={u.status} /></td>
                  <td>
                    <button className="btn-sm btn-info" onClick={() => handleEdit(u.uniform_id)}>Edit</button>
                    <button className="btn-sm btn-danger" onClick={() => handleDelete(u.uniform_id, u.name)}>Hapus</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pageInfo.total > 10 && (
        <div className="pagination">
          <button onClick={() => setPage(page - 1)} disabled={page === 1}>&larr; Previous</button>
          <span>Hal {page} / {pageInfo.last_page}</span>
          <button onClick={() => setPage(page + 1)} disabled={page === pageInfo.last_page}>Next &rarr;</button>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingId ? 'Edit Seragam' : 'Tambah Seragam Baru'}</h2>
            <form onSubmit={handleSubmit}>

              {/* Grup 1: Informasi Utama */}
              <div className="form-section-label">Informasi Utama</div>

              {isSuperadmin && !editingId && (
                <div className="form-group">
                  <label>Perusahaan *</label>
                  <select
                    value={formData.company_id}
                    onChange={(e) => {
                      const cid = e.target.value;
                      setFormData({ ...formData, company_id: cid, uniform_category_id: '' });
                      fetchFormCategories(cid);
                    }}
                    required
                  >
                    <option value="">Pilih perusahaan</option>
                    {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              {isSuperadmin && editingId && (
                <div className="form-group">
                  <label>Perusahaan</label>
                  <input
                    type="text"
                    value={companies.find((c) => String(c.company_id) === String(formData.company_id))?.name || formData.company?.name || '\u2014'}
                    disabled
                    style={{ background: '#f5f5f5', color: '#888', cursor: 'not-allowed' }}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Kategori *</label>
                <select
                  value={formData.uniform_category_id}
                  onChange={(e) => setFormData({ ...formData, uniform_category_id: e.target.value })}
                  required
                  disabled={!editingId && formCategoryList.length === 0}
                >
                  <option value="">
                    {!editingId && formCategoryList.length === 0
                      ? (isSuperadmin && !formData.company_id ? 'Pilih perusahaan dulu' : 'Tidak ada kategori tersedia')
                      : 'Pilih kategori'}
                  </option>
                  {(editingId ? categories : formCategoryList).map((cat) => (
                    <option key={cat.uniform_category_id} value={cat.uniform_category_id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Kode *</label>
                  <input type="text" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} placeholder="SHIRT-RED-L" required />
                </div>
                <div className="form-group">
                  <label>Nama *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Kemeja Merah Ukuran L" required />
                </div>
              </div>

              {/* Grup 2: Detail */}
              <div className="form-section-label">Detail</div>

              <div className="form-row">
                <div className="form-group">
                  <label>Ukuran</label>
                  <input type="text" value={formData.size ?? ''} onChange={(e) => setFormData({ ...formData, size: e.target.value })} placeholder="L, M, S, 30..." />
                </div>
                <div className="form-group">
                  <label>Warna</label>
                  <input type="text" value={formData.color ?? ''} onChange={(e) => setFormData({ ...formData, color: e.target.value })} placeholder="Red, Navy..." />
                </div>
              </div>

              <div className="form-group">
                <label>Target Gender</label>
                <select value={formData.gender_target} onChange={(e) => setFormData({ ...formData, gender_target: e.target.value })}>
                  <option value="all">Semua</option>
                  <option value="male">Laki-laki</option>
                  <option value="female">Perempuan</option>
                </select>
              </div>

              {/* Grup 3: Tambahan */}
              <div className="form-section-label">Tambahan</div>

              <div className="form-group">
                <label>Deskripsi</label>
                <textarea value={formData.description ?? ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Deskripsi singkat seragam..." />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                  <option value="active">Aktif</option>
                  <option value="inactive">Nonaktif</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">Simpan</button>
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Batal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniformsPage;
